// Reports API service for Cleancy Web App

import { API_VERSION } from "../constants";
import { ReportData } from "../types/global.d";
import { API_URL, fetchWithErrorHandling } from "../utils/api";
import { normalizeReportsTextFields } from "../utils/textNormalization";
import { fetchAllReports } from "./sync";

const readResponseError = async (response: Response) => {
  const responseText = await response.text();
  let responseData: unknown = responseText;

  if (responseText) {
    try {
      responseData = JSON.parse(responseText);
    } catch {
      responseData = responseText;
    }
  }

  const message =
    responseData && typeof responseData === "object" && "error" in responseData
      ? String((responseData as { error?: unknown }).error)
      : responseText || `HTTP error ${response.status}`;
  const error: any = new Error(message);
  error.status = response.status;
  error.responseData = responseData;
  error.responseText = responseText;
  throw error;
};

const wrapUploadError = (prefix: string, error: any): Error => {
  const wrappedError: any = new Error(
    `${prefix}: ${error instanceof Error ? error.message : String(error)}`
  );
  wrappedError.status = error?.status;
  wrappedError.responseData = error?.responseData;
  wrappedError.responseText = error?.responseText;
  return wrappedError;
};

/**
 * Fetch all reports from the server
 * Now uses the sync endpoint for consistency with mobile app
 */
export const listReports = async (): Promise<ReportData[]> => {
  try {
    const response = await fetchAllReports();

    // Handle both array and object response formats
    if (Array.isArray(response.reports)) {
      return normalizeReportsTextFields(response.reports);
    }

    // If reports is an object with nearby/distant (optimal strategy)
    if (
      response.reports &&
      typeof response.reports === "object" &&
      "nearby" in response.reports
    ) {
      const { nearby, distant } = response.reports as {
        nearby: ReportData[];
        distant: ReportData[];
      };
      return normalizeReportsTextFields([...nearby, ...distant]);
    }

    return [];
  } catch (error) {
    throw new Error(`Failed to get reports: ${error}`);
  }
};

/**
 * Delete a report by ID
 * Requires authentication token
 */
export const deleteReport = async (id: string, token: string): Promise<any> => {
  try {
    const data = await fetchWithErrorHandling(`${API_URL}/reports/delete`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id, api_version: API_VERSION }),
    });
    return data;
  } catch (error) {
    throw new Error(`Failed to delete report: ${error}`);
  }
};

// POST JSON with auth + server error-body parsing.
const postJsonWithAuth = async (
  path: string,
  body: Record<string, unknown>,
  token: string,
  errorPrefix: string
): Promise<any> => {
  try {
    const response = await fetch(`${API_URL}${path}`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      await readResponseError(response);
    }

    return await response.json();
  } catch (error) {
    throw wrapUploadError(errorPrefix, error);
  }
};

export interface SubmitReportPayload {
  imageKeys: string[];
  comment: string;
  geoposition: string; // JSON string: { latitude, longitude }
  locationName: string;
  amount: number;
  area: number;
  trashTypes: string; // JSON string: number[]
  action: number;
}

/**
 * Submit a new report whose images were already uploaded directly to GCS.
 * Sends only the resulting object keys + fields (small JSON, no gateway limit).
 */
export const submitReportDirect = async (
  payload: SubmitReportPayload,
  token: string
): Promise<any> => {
  return postJsonWithAuth(
    "/reports/submit",
    { ...payload, api_version: API_VERSION },
    token,
    "Failed to submit report"
  );
};

export interface SubmitCleanedPayload {
  _id: string;
  imageKeys?: string[];
  comment: string;
  amount?: number;
  cleaner?: number;
  useAdminImage?: boolean;
  adminImageType?: "not_found";
}

/**
 * Submit cleanup whose images were already uploaded directly to GCS (or, for
 * the admin placeholder, with no images at all).
 */
export const submitCleanedDirect = async (
  payload: SubmitCleanedPayload,
  token: string
): Promise<any> => {
  const { useAdminImage, ...rest } = payload;
  return postJsonWithAuth(
    "/reports/clean",
    {
      ...rest,
      // Server compares useAdminImage against the string "true".
      ...(useAdminImage ? { useAdminImage: "true" } : {}),
      api_version: API_VERSION,
    },
    token,
    "Failed to submit cleanup"
  );
};

// Edit/add image endpoints, direct-upload variant: images were already PUT to
// GCS, so we send only their pending object keys (small JSON, no gateway limit).
export const editReportImagesDirect = async (
  payload: { _id: string; imageKeys: string[] },
  token: string
): Promise<any> =>
  postJsonWithAuth(
    "/reports/editimages",
    { ...payload, api_version: API_VERSION },
    token,
    "Failed to upload report images"
  );

export const addReportImagesDirect = async (
  payload: { _id: string; imageKeys: string[] },
  token: string
): Promise<any> =>
  postJsonWithAuth(
    "/reports/addimages",
    { ...payload, api_version: API_VERSION },
    token,
    "Failed to upload report images"
  );

export const editCleanedImagesDirect = async (
  payload: { _id: string; imageKeys: string[] },
  token: string
): Promise<any> =>
  postJsonWithAuth(
    "/reports/editcleanedimages",
    { ...payload, api_version: API_VERSION },
    token,
    "Failed to upload cleanup images"
  );

export const addCleanedImagesDirect = async (
  payload: { _id: string; imageKeys: string[] },
  token: string
): Promise<any> =>
  postJsonWithAuth(
    "/reports/addcleanedimages",
    { ...payload, api_version: API_VERSION },
    token,
    "Failed to upload cleanup images"
  );

/**
 * Verify a report (admin only)
 * Requires authentication token
 */
export const verifyReport = async (id: string, token: string): Promise<any> => {
  try {
    const data = await fetchWithErrorHandling(`${API_URL}/reports/verify`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id, api_version: API_VERSION }),
    });
    return data;
  } catch (error) {
    throw new Error(`Failed to verify report: ${error}`);
  }
};

/**
 * Verify a cleanup (admin only)
 * Requires authentication token
 */
export const verifyCleaned = async (
  id: string,
  token: string
): Promise<any> => {
  try {
    const data = await fetchWithErrorHandling(`${API_URL}/reports/cleaned`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id, api_version: API_VERSION }),
    });
    return data;
  } catch (error) {
    throw new Error(`Failed to verify cleanup: ${error}`);
  }
};

/**
 * Edit a report
 * Requires authentication token
 */
export const editReport = async (
  reportUpdate: {
    _id: string;
    comment: string;
    locationName: string;
    geoposition: { latitude: number; longitude: number };
    area: number;
    amount: number;
    trashTypes: number[];
    action: number;
  },
  token: string
): Promise<any> => {
  try {
    const data = await fetchWithErrorHandling(`${API_URL}/reports/edit`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ...reportUpdate, api_version: API_VERSION }),
    });
    return data;
  } catch (error) {
    throw new Error(`Failed to edit report: ${error}`);
  }
};

/**
 * Mark a cleanup as not cleaned (admin only)
 * Removes the cleanup data from the report
 * Requires authentication token
 */
export const submitNotCleaned = async (
  id: string,
  token: string
): Promise<any> => {
  try {
    const data = await fetchWithErrorHandling(`${API_URL}/reports/notcleaned`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id, api_version: API_VERSION }),
    });
    return data;
  } catch (error) {
    throw new Error(`Failed to mark as not cleaned: ${error}`);
  }
};

/**
 * Edit cleanup data (admin or owner if not verified)
 * Requires authentication token
 */
export const editCleaned = async (
  cleanedData: {
    _id: string;
    comment: string;
    amount?: number;
    cleaner?: number;
    user?: string;
  },
  token: string
): Promise<any> => {
  try {
    const data = await fetchWithErrorHandling(
      `${API_URL}/reports/editcleaned`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ ...cleanedData, api_version: API_VERSION }),
      }
    );
    return data;
  } catch (error) {
    throw new Error(`Failed to edit cleanup: ${error}`);
  }
};
